import { useState } from "react"
import { useMeStore, type MeEntitlements } from "@/features/auth/hooks/useCurrentUser"
import { useCheckout } from "../hooks/useCheckout"
import { rupees } from "../plan"
import "./billing.css"

const DAY = 24 * 60 * 60 * 1000
/** The first month's last five days (day 25 on). */
const ENDING_FROM_MS = 5 * DAY
/** A renewal is news for a week. */
const RENEWED_FOR_MS = 7 * DAY
const RENEWED_KEY = "nira.billing.renewed_dismissed"

/** "14 November" in IST, or null for a bad or missing instant. */
export function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? null
    : new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "long", timeZone: "Asia/Kolkata" }).format(d)
}

const time = (iso: string | null | undefined): number | null => {
  const t = iso ? new Date(iso).getTime() : NaN
  return Number.isNaN(t) ? null : t
}

/** Trigger 1: the free first month (10 research messages a day) ends within five days. */
export function firstMonthEnding(e: MeEntitlements | null | undefined, now = Date.now()): string | null {
  if (e?.plan_state !== "free_month") return null
  const end = time(e.first_month_ends_at)
  if (end === null || end <= now || end - now > ENDING_FROM_MS) return null
  return shortDate(e.first_month_ends_at)
}

const readDismissed = (): string | null => {
  try { return window.localStorage.getItem(RENEWED_KEY) } catch { return null }
}
const writeDismissed = (v: string): void => {
  try { window.localStorage.setItem(RENEWED_KEY, v) } catch { /* shown again next visit */ }
}

/** Where a charge is news: a paid plan, charged in the last week, not yet dismissed. */
export function renewedCharge(e: MeEntitlements | null | undefined, now = Date.now()): string | null {
  const r = e?.renewal
  if (!r?.last_charged_at || (e?.plan_state !== "plus" && e?.plan_state !== "pro")) return null
  const at = time(r.last_charged_at)
  if (at === null || now - at > RENEWED_FOR_MS || at > now) return null
  return r.last_charged_at
}

/**
 * The billing notices, mounted once in the Nira shell: the first month ending (trigger 1), a failed
 * renewal in its three days' grace (trigger 17), and a renewal that went through (once per charge).
 */
export function BillingBanners() {
  const me = useMeStore((s) => s.me)
  const checkout = useCheckout()
  const [dismissed, setDismissed] = useState<string | null>(readDismissed)
  const e = me?.entitlements
  if (!me || !me.billing_enabled || !e || e.exempt) return null

  const ending = firstMonthEnding(e)
  const grace = e.plan_state === "grace"
  const charged = renewedCharge(e)
  const renewed = charged && charged !== dismissed ? charged : null
  if (!ending && !grace && !renewed) return null

  const plan = e.plan_code === "plus" ? "Plus" : "Pro"
  const payBy = shortDate(e.grace_ends_at)
  const payUrl = e.renewal?.pay_url
  const r = e.renewal
  const amount = r?.last_amount_minor != null ? ` ${rupees(r.last_amount_minor)}` : ""
  const next = shortDate(r?.next_charge_at)
  return (
    <div className="nbb-stack">
      {ending && (
        <div className="nbb" role="status" data-trigger="1">
          <p>Your free first month ends on {ending}. After that: 5 questions a month free, 40 with Plus, or 10 a day with Pro.</p>
          <div className="nbb-act">
            <button type="button" className="nr-btn" disabled={!!checkout.busy} onClick={() => { void checkout.subscribe("plus") }}>Plus</button>
            <button type="button" className="nr-btn" data-variant="primary" disabled={!!checkout.busy} onClick={() => { void checkout.subscribe("pro") }}>Pro</button>
          </div>
          {checkout.error && <p role="alert">{checkout.error}</p>}
        </div>
      )}
      {grace && (
        <div className="nbb warn" role="alert" data-trigger="17">
          <p>Your payment didn&apos;t go through. {payBy ? `Pay by ${payBy}` : "Pay soon"} to keep {plan}. Saved workspaces stay.</p>
          {payUrl && <div className="nbb-act"><a className="nr-btn" data-variant="primary" href={payUrl}>Pay now</a></div>}
        </div>
      )}
      {renewed && (
        <div className="nbb" role="status" data-notice="renewed">
          <p>Renewed:{amount} charged on {shortDate(renewed)}.{next ? ` Next on ${next}.` : ""}</p>
          <div className="nbb-act">
            <button type="button" className="nbb-x" onClick={() => { writeDismissed(renewed); setDismissed(renewed) }}>Got it</button>
          </div>
        </div>
      )}
    </div>
  )
}
